import { useState, useEffect, useRef } from 'react';
import { gql, useMutation } from '@apollo/client';
import Link from 'next/link';
import Modal from './modal';
import UpgradeTeamPlanModal from './upgrade-plan-modal';
import { Button, ErrorBanner } from '../../../lib/ui';
import classnames from 'classnames';
import { ReadOnlyEditor } from '../editor';
import { inlineCss } from '../../inline-css';
import CodeFile from '../../../lib/icons/code-file';
import DownloadFile from '../../../lib/icons/download-file';
import Copy from '../../../lib/icons/copy';
import PdfFile from '../../../lib/icons/pdf-file';
import { trackProductEvent, trackLimitHit, trackUpgradeCtaClicked, plansUrlWithUpgradeSource } from '../../analytics';

const EXPORT_TEMPLATE = gql`
  mutation ExportTemplate($templateId: ID!) {
    exportTemplate(input: { templateId: $templateId }) {
      success
      exportCount
      maxExports
    }
  }
`;

const downloadFile = (data, filename) => {
  const file = new Blob([data], { type: 'text/html' });
  if (window.navigator.msSaveOrOpenBlob) {
    window.navigator.msSaveOrOpenBlob(file, filename);
  } else {
    const a = document.createElement('a');
    const url = URL.createObjectURL(file);
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    setTimeout(function () {
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    }, 0);
  }
};

function DownloadTemplateModal({ toggle, isOpen, data, plan = 'FREE' }) {
  const [content, setContent] = useState('');
  const [isCopied, setCopied] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [step, setStep] = useState<'confirm' | 'viewer'>('confirm');
  const [hasExported, setHasExported] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportingAction, setExportingAction] = useState<'view' | 'download' | 'copy' | null>(null);
  const pdfIframeRef = useRef<HTMLIFrameElement | null>(null);

  const [doExport] = useMutation(EXPORT_TEMPLATE);

  const isFree = plan === 'FREE';
  const isPaid = !isFree;
  const filename = data?.name || 'postdrop-template';

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setCopied(false);
    setIsGeneratingPDF(false);
    setFormError(null);
    setHasExported(false);
    setIsExporting(false);
    setExportingAction(null);
    setShowUpgradeModal(false);
    setStep(isPaid ? 'viewer' : 'confirm');
    try {
      const result = inlineCss(data?.rawTemplate || '', data?.css || '');
      setContent(result);
    } catch (error) {
      setFormError('Failed to process template. Please try again.');
      setContent('');
    }
  }, [isOpen, data, isPaid]);

  const consumeExport = async (): Promise<boolean> => {
    if (hasExported) return true;
    setIsExporting(true);
    setFormError(null);
    try {
      await doExport({
        variables: { templateId: data?.id || '' },
      });
      setHasExported(true);
      setIsExporting(false);
      return true;
    } catch (error) {
      setIsExporting(false);
      if (isPaid) {
        console.error('Export tracking failed:', error);
        setHasExported(true);
        return true;
      }
      const msg = error?.message || '';
      if (msg.toLowerCase().includes('export limit exceeded')) {
        trackLimitHit({
          limit_type: 'exports_monthly',
          plan,
          source: 'download_template_modal',
        });
        setShowUpgradeModal(true);
        return false;
      }
      setFormError(msg || 'Export failed. Please try again.');
      return false;
    }
  };

  const withExportAction = async (action: 'view' | 'download' | 'copy', fn: () => void | Promise<void>) => {
    setExportingAction(action);
    const ok = await consumeExport();
    if (!ok) { setExportingAction(null); return; }
    setExportingAction(null);
    await fn();
    const actionProp =
      action === 'view'
        ? 'view'
        : action === 'download'
          ? 'download_html'
          : 'copy_html';
    trackProductEvent('Template Exported', {
      template_id: data?.id,
      action: actionProp,
    });
  };

  const downloadAsFile = () => withExportAction('download', () => {
    // @ts-ignore
    downloadFile(content, `${filename}.html`);
  });

  const copyTemplate = () => withExportAction('copy', async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
    } catch (error) {
      setFormError('Failed to copy to clipboard. Please try again.');
    }
  });

  const viewInlinedCode = () => withExportAction('view', () => {
    setStep('viewer');
  });

  const waitForImages = (iframeDocument: Document) => {
    const images = Array.from(iframeDocument.images);
    if (images.length === 0) return Promise.resolve();

    return Promise.all(
      images.map(img => {
        if (img.complete) return Promise.resolve();
        return new Promise<void>((resolve) => {
          let timeoutId: NodeJS.Timeout;
          const resolver = () => {
            clearTimeout(timeoutId);
            resolve();
          };
          img.onload = resolver;
          img.onerror = resolver;
          timeoutId = setTimeout(resolver, 5000);
        });
      })
    );
  };

  const downloadAsPDF = async () => {
    if (!content || isGeneratingPDF) return;

    setIsGeneratingPDF(true);
    setFormError(null);
    let iframe: HTMLIFrameElement | null = null;

    try {
      iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.left = '-9999px';
      iframe.style.top = '-9999px';
      iframe.style.width = '600px';
      iframe.style.height = '800px';
      iframe.style.border = 'none';
      iframe.setAttribute('sandbox', 'allow-same-origin');
      iframe.srcdoc = content;
      document.body.appendChild(iframe);
      pdfIframeRef.current = iframe;

      await new Promise<void>((resolve, reject) => {
        const timeoutId = setTimeout(() => {
          reject(new Error('Iframe load timeout'));
        }, 15000);

        iframe.onload = () => {
          clearTimeout(timeoutId);
          setTimeout(() => {
            resolve();
          }, 500);
        };

        iframe.onerror = () => {
          clearTimeout(timeoutId);
          reject(new Error('Iframe failed to load'));
        };
      });

      const iframeDocument = iframe.contentDocument || iframe.contentWindow?.document;
      if (!iframeDocument || !iframeDocument.body) {
        throw new Error('Could not access iframe content');
      }

      await waitForImages(iframeDocument);
      await new Promise(resolve => setTimeout(resolve, 500));

      const html2canvas = (await import('html2canvas-pro')).default;
      const { jsPDF } = await import('jspdf');

      const iframeWindow = iframe.contentWindow;
      if (!iframeWindow) {
        throw new Error('Could not access iframe window');
      }

      const h2cStyle = document.createElement('style');
      document.head.appendChild(h2cStyle);
      h2cStyle.sheet?.insertRule('body > div:last-child img { display: inline-block; }');

      let canvas;
      try {
        canvas = await html2canvas(iframeDocument.body, {
          scale: 3,
          useCORS: true,
          allowTaint: false,
          logging: false,
          backgroundColor: '#ffffff',
          windowWidth: 600,
          windowHeight: iframeDocument.body.scrollHeight || 800,
          scrollX: 0,
          scrollY: 0,
          width: 600,
          height: iframeDocument.body.scrollHeight || 800,
          onclone: (clonedDoc) => {
            const clonedBody = clonedDoc.body;
            if (clonedBody) {
              clonedBody.style.width = '600px';
              clonedBody.style.margin = '0';
              clonedBody.style.padding = '0';
            }

            clonedDoc.querySelectorAll('td').forEach(td => {
              const anchor = td.querySelector('a[style*="padding"], a[style*="inline-block"]') as HTMLElement | null;
              if (anchor) {
                anchor.style.lineHeight = 'normal';
                anchor.style.overflow = 'visible';
                td.style.verticalAlign = 'middle';
                td.style.overflow = 'visible';
              }
            });

            clonedDoc.querySelectorAll('img').forEach(img => {
              const rect = img.getBoundingClientRect();
              if (rect.width && rect.height) {
                img.setAttribute('width', String(Math.round(rect.width)));
                img.setAttribute('height', String(Math.round(rect.height)));
                img.style.width = `${Math.round(rect.width)}px`;
                img.style.height = `${Math.round(rect.height)}px`;
              }
            });

            clonedDoc.querySelectorAll('table').forEach(table => {
              table.style.display = 'table';
              table.style.borderCollapse = 'separate';
            });
            clonedDoc.querySelectorAll('tbody').forEach(tbody => {
              tbody.style.display = 'table-row-group';
            });
            clonedDoc.querySelectorAll('tr').forEach(tr => {
              tr.style.display = 'table-row';
            });
          },
        });
      } catch (canvasError) {
        throw canvasError;
      } finally {
        h2cStyle.remove();
      }

      const PX_TO_MM = 0.264583;
      const canvasWidthMm = canvas.width * PX_TO_MM;
      const canvasHeightMm = canvas.height * PX_TO_MM;

      const pdf = new jsPDF({
        unit: 'mm',
        format: [canvasWidthMm, canvasHeightMm],
        orientation: 'portrait',
      });

      const imgData = canvas.toDataURL('image/png');
      pdf.addImage(imgData, 'PNG', 0, 0, canvasWidthMm, canvasHeightMm);
      pdf.save(`${filename}.pdf`);
    } catch (error) {
      const errorMessage = error instanceof Error
        ? error.message
        : 'Failed to generate PDF. Please try again.';
      setFormError(errorMessage);
    } finally {
      if (iframe && iframe.parentNode) {
        document.body.removeChild(iframe);
      }
      pdfIframeRef.current = null;
      setIsGeneratingPDF(false);
    }
  };

  const loadingDots = (
    <span className="w-4 inline-block">
      <span className="animate-dot">.</span>
      <span className="animate-dot [animation-delay:0.2s]">.</span>
      <span className="animate-dot [animation-delay:0.4s]">.</span>
    </span>
  );

  const pdfButtonLabel = (label: string) =>
    isGeneratingPDF ? <>Generating PDF{loadingDots}</> : label;

  const confirmStep = (
    <div className="text-left">
      <div className="sm:flex sm:items-start mb-4">
        <h3
          className="text-base font-semibold tracking-tight text-neutral-900"
          id="modal-headline">
          Export Email Template
        </h3>
      </div>
      {isFree && (
        <p className="text-sm text-neutral-500 mb-6">
          Exports are limited to 10 / Month on the Free plan.{' '}
          <Link
            href={plansUrlWithUpgradeSource('download_template_modal', 'exports_monthly')}
            target="_blank"
            className="text-blue underline hover:opacity-80"
            onClick={() =>
              trackUpgradeCtaClicked({
                source: 'download_template_modal',
                limit_type: 'exports_monthly',
                plan,
              })
            }
          >
            Upgrade
          </Link>{' '}
          for unlimited exports.
        </p>
      )}
      {formError && <ErrorBanner title={formError} />}
      <div className="mt-6 mb-1 flex flex-wrap gap-2">
        <Button tag="button" type="button" disabled={isExporting} onClick={viewInlinedCode}>
          <CodeFile className="w-4 h-4 mr-1.5 -ml-0.5" />
          {exportingAction === 'view' ? <>Exporting{loadingDots}</> : 'View Inlined Code'}
        </Button>
        <Button tag="button" type="button" outline disabled={isExporting} onClick={downloadAsFile}>
          <DownloadFile className="w-4 h-4 mr-1.5 -ml-0.5" />
          {exportingAction === 'download' ? <>Exporting{loadingDots}</> : 'Download HTML'}
        </Button>
        <Button tag="button" type="button" outline disabled={isExporting} onClick={copyTemplate}>
          <Copy className="w-4 h-4 mr-1.5 -ml-0.5" />
          {exportingAction === 'copy' ? <>Exporting{loadingDots}</> : (isCopied ? 'Copied!' : 'Copy HTML')}
        </Button>
        <Button
          tag="button"
          type="button"
          outline
          disabled={isGeneratingPDF || !content}
          onClick={downloadAsPDF}>
          <PdfFile className="w-4 h-4 mr-1.5 -ml-0.5" />
          {pdfButtonLabel('Download PDF (free)')}
        </Button>
      </div>
    </div>
  );

  const viewerStep = (
    <div className="text-left">
      <div className="sm:flex sm:items-start mb-4">
        {isFree && step === 'viewer' && !isPaid && (
          <button
            type="button"
            onClick={() => setStep('confirm')}
            className="mr-3 text-neutral-400 hover:text-neutral-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}
        <h3
          className="text-base font-semibold tracking-tight text-neutral-900"
          id="modal-headline">
          Export Email Template
        </h3>
      </div>
      <p className="block text-sm font-medium text-neutral-800 my-6">
        Inlined HTML/CSS
      </p>
      {formError && <ErrorBanner title={formError} />}
      <ReadOnlyEditor value={content} height="500px" />
      <div className="mt-8 mb-1 sm:flex">
        <Button tag="button" type="button" disabled={isExporting} onClick={downloadAsFile}>
          Download HTML
        </Button>
        <Button
          onClick={copyTemplate}
          tag="button"
          type="button"
          outline
          disabled={isExporting}
          className="ml-2">
          {isCopied ? 'Copied!' : 'Copy HTML'}
        </Button>
        <Button
          onClick={downloadAsPDF}
          tag="button"
          type="button"
          outline
          className={classnames('ml-2', { '!bg-blue-light !opacity-60': isGeneratingPDF || !content })}
          disabled={isGeneratingPDF || !content}>
          {pdfButtonLabel('Download PDF')}
        </Button>
      </div>
    </div>
  );

  return (
    <>
      <Modal toggle={toggle} isOpen={isOpen} size="sm:max-w-4xl" closeButton>
        {step === 'confirm' ? confirmStep : viewerStep}
      </Modal>
      <UpgradeTeamPlanModal
        toggle={() => setShowUpgradeModal(false)}
        isOpen={showUpgradeModal}
        description="You've used all 10 of your monthly exports. Upgrade for unlimited exports."
        upgradeSource="download_template_modal"
        limitType="exports_monthly"
        plan={plan}
      />
    </>
  );
}

export default DownloadTemplateModal;
